/**
 * Processing timeline — the single source of truth for "what is the system
 * doing right now" during a card capture.
 *
 * The seller's photo passes through three independent subsystems (the studio
 * render in a Web Worker, the AI identity scan, the AI grader). Each reports
 * into this store, so the UI can show one honest, ordered timeline instead of
 * three separate spinners.
 *
 * Deliberately framework-free: it is a tiny observable store consumed with
 * `useSyncExternalStore`, so worker callbacks and server-function promises can
 * publish without caring about React lifecycles.
 */

import type { StudioStage } from "./studio";

export type TimelineStageId =
  | "upload"
  | "rotate"
  | "detect"
  | "crop"
  | "enhance"
  | "ocr"
  | "game"
  | "details"
  | "grade"
  | "complete";

export type TimelineStageState = "pending" | "active" | "done" | "failed" | "skipped";

export type TimelineStage = {
  id: TimelineStageId;
  label: string;
  state: TimelineStageState;
  /** Populated while active/done so the UI can show real durations. */
  startedAt: number | null;
  endedAt: number | null;
  detail: string | null;
};

export type TimelineSnapshot = {
  runId: number;
  /** True between the first stage starting and the run settling. */
  running: boolean;
  stages: TimelineStage[];
  activeId: TimelineStageId | null;
  failedId: TimelineStageId | null;
  /** 0–1 across the whole run, weighted by how long each stage really takes. */
  progress: number;
  /** Milliseconds remaining, or null when it cannot be estimated yet. */
  etaMs: number | null;
  startedAt: number | null;
  endedAt: number | null;
  /** Seller-safe explanation of the failure, never a raw error. */
  failureMessage: string | null;
  /** True when the failed stage knows how to resume itself. */
  canRetryStage: boolean;
  /** True when the whole scan can be restarted without re-uploading. */
  canRestart: boolean;
  /** Restored from a previous session rather than started in this tab. */
  restored: boolean;
};

/**
 * Typical duration of each stage on a mid-range phone, used to seed the ETA
 * before we have measurements. Real durations replace these as the run
 * progresses, so the estimate converges rather than jumping around.
 */
const BASELINE_MS: Record<TimelineStageId, number> = {
  upload: 3500,
  rotate: 600,
  detect: 900,
  crop: 700,
  enhance: 1800,
  ocr: 4500,
  game: 700,
  details: 2500,
  grade: 6000,
  complete: 200,
};

/**
 * Share of the overall percentage each stage owns. Weighted rather than
 * "1/11 per stage" so the number the seller reads tracks real elapsed effort:
 * upload and grading dominate, `game` is instant.
 */
const WEIGHT: Record<TimelineStageId, number> = {
  upload: 15,
  rotate: 5,
  detect: 6,
  crop: 6,
  enhance: 8,
  ocr: 15,
  game: 4,
  details: 10,
  grade: 18,
  complete: 3,
};

const TOTAL_WEIGHT = Object.values(WEIGHT).reduce((a, b) => a + b, 0);


const ORDER: { id: TimelineStageId; label: string }[] = [
  { id: "upload", label: "Upload" },
  { id: "rotate", label: "Auto rotate" },
  { id: "detect", label: "Detect card" },
  { id: "crop", label: "Crop" },
  { id: "enhance", label: "Enhance image" },
  { id: "ocr", label: "OCR" },
  { id: "game", label: "Detect game" },
  { id: "details", label: "Extract card details" },
  { id: "grade", label: "AI grade" },
  { id: "complete", label: "Complete" },
];

/** Studio worker stages map onto the seller-facing timeline. */
const STUDIO_MAP: Record<StudioStage, TimelineStageId | null> = {
  decoding: "rotate",
  detecting: "detect",
  rectifying: "crop",
  encoding: null,
  done: null,
};

export function timelineStageForStudioStage(stage: StudioStage): TimelineStageId | null {
  return STUDIO_MAP[stage];
}

function blankStages(): TimelineStage[] {
  return ORDER.map(({ id, label }) => ({
    id,
    label,
    state: "pending" as TimelineStageState,
    startedAt: null,
    endedAt: null,
    detail: null,
  }));
}

/** Stages that only their owner may complete — never inferred by progress. */
const EXPLICIT_ONLY = new Set<TimelineStageId>();

let runId = 0;
let stages = blankStages();
let startedAt: number | null = null;
let endedAt: number | null = null;
let failureMessage: string | null = null;
let restored = false;
/** Measured durations from earlier runs in this session, for a sharper ETA. */
const measured = new Map<TimelineStageId, number>();

/**
 * Resume handlers published by whichever subsystem owns a stage. They are what
 * makes "Retry current stage" possible without asking for the photo again.
 */
const retryHandlers = new Map<TimelineStageId, () => void | Promise<void>>();
let restartHandler: (() => void | Promise<void>) | null = null;

const listeners = new Set<() => void>();
let snapshot: TimelineSnapshot = computeSnapshot();

function indexOf(id: TimelineStageId): number {
  return ORDER.findIndex((s) => s.id === id);
}

function expectedMs(id: TimelineStageId): number {
  const seen = measured.get(id);
  // Blend the baseline with what this device actually did, so a slow phone is
  // reflected without one outlier dominating the estimate.
  return seen == null ? BASELINE_MS[id] : Math.round(seen * 0.7 + BASELINE_MS[id] * 0.3);
}

function computeSnapshot(): TimelineSnapshot {
  const active = stages.find((s) => s.state === "active") ?? null;
  const failed = stages.find((s) => s.state === "failed") ?? null;

  // Weighted percentage: an active stage earns half its weight so the number
  // always moves forward, never sits frozen through a long AI call.
  let earned = 0;
  for (const s of stages) {
    if (s.state === "done" || s.state === "skipped") earned += WEIGHT[s.id];
    else if (s.state === "active") earned += WEIGHT[s.id] * 0.5;
  }
  const progress = Math.min(1, earned / TOTAL_WEIGHT);

  let etaMs: number | null = null;
  if (active && !failed) {
    let remaining = 0;
    const activeIdx = indexOf(active.id);
    for (let i = activeIdx; i < ORDER.length; i++) {
      const stage = stages[i]!;
      if (stage.state === "done" || stage.state === "skipped") continue;
      let cost = expectedMs(stage.id);
      if (i === activeIdx && stage.startedAt) {
        // Subtract time already spent, but never promise less than a second.
        cost = Math.max(1000, cost - (Date.now() - stage.startedAt));
      }
      remaining += cost;
    }
    etaMs = remaining;
  }

  return {
    runId,
    running: startedAt !== null && endedAt === null,
    stages: stages.map((s) => ({ ...s })),
    activeId: active?.id ?? null,
    failedId: failed?.id ?? null,
    progress,
    etaMs,
    startedAt,
    endedAt,
    failureMessage: failed ? failureMessage : null,
    canRetryStage: failed ? retryHandlers.has(failed.id) : false,
    canRestart: restartHandler !== null,
    restored,
  };
}

function publish() {
  snapshot = computeSnapshot();
  persist();
  for (const l of [...listeners]) {
    try {
      l();
    } catch {
      /* a broken subscriber must never stall the pipeline */
    }
  }
}

// ---------------------------------------------------------------------------
// Persistence — the timeline survives refreshes, tab switches and navigation.
// ---------------------------------------------------------------------------

const STORAGE_KEY = "bidnova.timeline.v1";
export const TIMELINE_TTL_MS = 30 * 60 * 1000;

type PersistedTimeline = {
  savedAt: number;
  runId: number;
  stages: TimelineStage[];
  startedAt: number | null;
  endedAt: number | null;
  failureMessage: string | null;
};

function persist() {
  try {
    if (typeof localStorage === "undefined") return;
    if (startedAt === null) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    const payload: PersistedTimeline = {
      savedAt: Date.now(),
      runId,
      stages,
      startedAt,
      endedAt,
      failureMessage,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* storage full or blocked (private mode) — persistence is best-effort */
  }
}

/**
 * Rehydrate the last run when the seller comes back within the TTL. An
 * in-flight stage is marked failed-but-resumable, because no worker survived
 * the reload; the retry controls then continue from exactly that stage.
 */
export function restoreTimeline(): TimelineSnapshot | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as PersistedTimeline;
    if (!saved?.stages?.length || Date.now() - saved.savedAt > TIMELINE_TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    runId = saved.runId;
    startedAt = saved.startedAt;
    endedAt = saved.endedAt;
    failureMessage = saved.failureMessage;
    restored = true;
    stages = saved.stages.map((s) =>
      s.state === "active"
        ? { ...s, state: "pending" as TimelineStageState, startedAt: null, endedAt: null }
        : s,
    );
    publish();
    return snapshot;
  } catch {
    return null;
  }
}

export function subscribeTimeline(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getTimelineSnapshot(): TimelineSnapshot {
  return snapshot;
}

/** Recompute so the countdown ticks while a long stage is running. */
export function refreshTimeline(): void {
  if (snapshot.running) publish();
}


/** Stage owners register how to resume themselves. */
export function registerStageRetry(id: TimelineStageId, fn: () => void | Promise<void>): void {
  retryHandlers.set(id, fn);
  publish();
}

export function registerTimelineRestart(fn: (() => void | Promise<void>) | null): void {
  restartHandler = fn;
  publish();
}

/**
 * Re-run the stage that failed (or, after a refresh, the first unfinished
 * one). Completed stages and their results are kept — the seller never
 * re-uploads.
 */
export function retryCurrentStage(): boolean {
  const target =
    stages.find((s) => s.state === "failed") ?? stages.find((s) => s.state === "pending");
  if (!target) return false;
  const handler = retryHandlers.get(target.id);
  if (!handler) return false;
  failureMessage = null;
  endedAt = null;
  stages = stages.map((s) =>
    s.id === target.id ? { ...s, state: "pending", startedAt: null, endedAt: null, detail: null } : s,
  );
  publish();
  void Promise.resolve(handler()).catch(() => {
    /* the handler reports its own failure through failStage */
  });
  return true;
}

/** Start the whole scan again from the already-uploaded photo. */
export function restartTimelineRun(): boolean {
  if (!restartHandler) return false;
  void Promise.resolve(restartHandler()).catch(() => undefined);
  return true;
}

/** The stage the retry button would resume, for labelling the control. */
export function resumableStage(): TimelineStage | null {
  return (
    stages.find((s) => s.state === "failed") ?? stages.find((s) => s.state === "pending") ?? null
  );
}

/** Begin a new run. Any previous run's state is discarded. */
export function startTimelineRun(): number {
  runId += 1;
  stages = blankStages();
  startedAt = Date.now();
  endedAt = null;
  failureMessage = null;
  restored = false;
  publish();
  return runId;
}

export function resetTimeline(): void {
  stages = blankStages();
  startedAt = null;
  endedAt = null;
  failureMessage = null;
  restored = false;
  publish();
}


/**
 * Mark a stage as running. Earlier pending stages are closed off as done, so
 * a subsystem that skips ahead (for example an already-cropped re-scan) never
 * leaves an orphaned spinner behind it.
 */
export function beginStage(id: TimelineStageId, detail?: string): void {
  if (startedAt === null) {
    startedAt = Date.now();
    endedAt = null;
  }
  const idx = indexOf(id);
  if (idx < 0) return;
  const now = Date.now();
  stages = stages.map((s, i) => {
    // Guarded stages report their own outcome rather than being inferred as
    // done just because a later stage started.
    if (i < idx && EXPLICIT_ONLY.has(s.id)) return s;
    if (i < idx && s.state === "pending") return { ...s, state: "done", endedAt: now };
    if (i < idx && s.state === "active") {
      record(s, now);
      return { ...s, state: "done", endedAt: now };
    }
    if (i === idx) {
      if (s.state === "done") return s;
      return { ...s, state: "active", startedAt: s.startedAt ?? now, endedAt: null, detail: detail ?? s.detail };
    }
    return s;
  });
  publish();
}

function record(stage: TimelineStage, at: number) {
  if (!stage.startedAt) return;
  const took = at - stage.startedAt;
  if (took > 40 && took < 120_000) measured.set(stage.id, took);
}

export function completeStage(id: TimelineStageId, detail?: string): void {
  const now = Date.now();
  const idx = indexOf(id);
  stages = stages.map((s, i) => {
    if (i !== idx) return s;
    record(s, now);
    return { ...s, state: "done", endedAt: now, detail: detail ?? s.detail };
  });
  if (id === "complete") endedAt = now;
  publish();
}

/** A stage the run legitimately does not need (for example a non-card photo). */
export function skipStage(id: TimelineStageId, detail?: string): void {
  const idx = indexOf(id);
  stages = stages.map((s, i) =>
    i === idx ? { ...s, state: "skipped", endedAt: Date.now(), detail: detail ?? s.detail } : s,
  );
  publish();
}

/**
 * Mark a stage as failed. `detail` is the short label shown on the row;
 * `sellerMessage` is the calm explanation shown in the header. Raw error text
 * belongs in the diagnostics bus, never here.
 */
export function failStage(id: TimelineStageId, detail?: string, sellerMessage?: string): void {
  const idx = indexOf(id);
  const now = Date.now();
  stages = stages.map((s, i) =>
    i === idx ? { ...s, state: "failed", endedAt: now, detail: detail ?? "Failed" } : s,
  );
  failureMessage = sellerMessage ?? detail ?? "That step did not finish.";
  endedAt = now;
  publish();
}


/** Close the run successfully — everything still pending becomes done. */
export function finishTimeline(): void {
  const now = Date.now();
  stages = stages.map((s) => {
    if (s.state === "failed" || s.state === "skipped") return s;
    if (s.state === "active") record(s, now);
    return s.state === "done" ? s : { ...s, state: "done", endedAt: now };
  });
  endedAt = now;
  publish();
}

/** Human-readable countdown, e.g. "about 12s left". */
export function formatEta(ms: number | null): string | null {
  if (ms == null || ms <= 0) return null;
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `about ${Math.max(1, secs)}s left`;
  const mins = Math.floor(secs / 60);
  const rest = secs % 60;
  return rest ? `about ${mins}m ${rest}s left` : `about ${mins}m left`;
}

export const TIMELINE_ORDER = ORDER;
